import { confirmTemplateNavigation, resetTemplateNavigation } from '~/utils/emailTemplates/navigation'
import * as Sentry from '@sentry/browser'
import Storage from '~/util/storage'
import {
	SECURITY_INCIDENT_AUTHENTICATED_BANNER_STORAGE_KEY,
	SECURITY_INCIDENT_LOGIN_BANNER_STORAGE_KEY
} from '~/constants/securityIncidentBanner'
import { isExpectedAuthenticationFailure } from '~/utils/authenticationFailure'
import { getSafeRedirectPath } from '~/utils/safeRedirect'

const SECURITY_INCIDENT_BANNER_STORAGE_KEYS = [
	SECURITY_INCIDENT_AUTHENTICATED_BANNER_STORAGE_KEY,
	SECURITY_INCIDENT_LOGIN_BANNER_STORAGE_KEY
]

function clearImpersonationMarker() {
	if (typeof window !== 'undefined') {
		try {
			window.localStorage.removeItem('impersonation_data')
		} catch {
			// Storage access must not prevent authenticated state from being cleared.
		}
	}
}

function clearUserScopedState(commit) {
	clearImpersonationMarker()
	commit('removeToken')
	commit('setUser', null)
	commit('applicantProfile/setApplicantProfile', null, { root: true })
	commit('filterCriteria/setFilterCriteria', null, { root: true })
	commit('organization/setOrganization', null, { root: true })
	commit('jobOffer/RESET_STATE', null, { root: true })
	commit('application/RESET_STATE', null, { root: true })
	commit('notification/RESET_STATE', null, { root: true })
	commit('activateJobOffer/RESET_STATE', null, { root: true })
}

function preserveSecurityIncidentBannerDismissals() {
	if (!process.client) {
		return {}
	}

	return SECURITY_INCIDENT_BANNER_STORAGE_KEYS.reduce((dismissals, key) => {
		const value = window.localStorage.getItem(key)

		if (value !== null) {
			dismissals[key] = value
		}

		return dismissals
	}, {})
}

function restoreSecurityIncidentBannerDismissals(dismissals) {
	if (!process.client) {
		return
	}

	Object.entries(dismissals).forEach(([key, value]) => {
		window.localStorage.setItem(key, value)
	})
}

export const state = () => {
	return {
		user: Storage.get('user'),
		token: Storage.get('auth-access-token')
	}
}

export const mutations = {
	setToken(state, token) {
		state.token = token
		Storage.set('auth-access-token', token)
	},

	removeToken(state) {
		state.token = null
		Storage.remove('auth-access-token')
	},

	setUser(state, user) {
		state.user = user
		Storage.set('user', user)

		if (!user) {
			Sentry.setUser(null)
			return
		}

		Sentry.setUser({
			id: user.id,
			username: user.fullName,
			email: user.email,
			organization: user.current_organization
				? user.current_organization.name
				: null,
			current_role: user.current_organization
				? user.current_organization.role_in_organization
				: null
		})
	},

	setCurrentOrganization(state, organization) {
		if (!state.user) {
			return
		}

		const previousOrganization = state.user.current_organization || {}
		const currentOrganization =
			String(previousOrganization.id) === String(organization?.id)
				? previousOrganization
				: {}
		const userScopedFields = [
			'permissions',
			'member',
			'member_id',
			'role_in_organization'
		]
		const mergedOrganization = {
			...currentOrganization,
			...organization
		}

		userScopedFields.forEach(field => {
			if (
				organization &&
				!Object.prototype.hasOwnProperty.call(organization, field) &&
				Object.prototype.hasOwnProperty.call(currentOrganization, field)
			) {
				mergedOrganization[field] = currentOrganization[field]
			}
		})

		const organizations = state.user.organizations || []
		const knownOrganization = organizations.some(
			item => String(item.id) === String(organization.id)
		)
		state.user = {
			...state.user,
			current_organization: mergedOrganization,
			organizations: knownOrganization
				? organizations.map(item =>
						String(item.id) === String(organization.id)
							? mergedOrganization
							: item
				  )
				: [...organizations, mergedOrganization]
		}
		Storage.set('user', state.user)
	}
}

export const getters = {
	loggedIn(state) {
		return state.token !== null
	},
	isVerified(state) {
		return state.user?.verified
	},
	user(state) {
		return state.user
	},
	fullName(state) {
		return `${state.user.firstname} ${state.user.lastname}`
	},
	hasPermissionTo: state => permission => {
		const permissions = state.user?.current_organization?.permissions

		return (
			Array.isArray(permissions) &&
			permissions.includes(permission)
		)
	},
	userIsApplicant(state) {
		return state.user !== null && state.user.applicant_profile !== null
	}
}

export const actions = {
	handleUnauthorized({ commit }) {
		clearUserScopedState(commit)

		const router = this.$router
		if (!router) {
			return
		}

		if (router.currentRoute?.path === '/auth/login') {
			router.go(0)
			return
		}

		const currentRoute = router.currentRoute
		const currentPath = currentRoute?.path
		const redirectPath = getSafeRedirectPath(currentRoute?.fullPath, null)
		const shouldPreserveRedirect =
			redirectPath &&
			typeof currentPath === 'string' &&
			currentPath !== '/auth' &&
			!currentPath.startsWith('/auth/')
		const loginRoute = shouldPreserveRedirect
			? {
					path: '/auth/login',
					query: { redirect: redirectPath }
			  }
			: '/auth/login'

		// Nuxt's push returns undefined, so wait for its navigation callbacks
		// before reloading the destination rather than the current page.
		return new Promise((resolve, reject) => {
			const navigation = router.push(loginRoute, resolve, reject)
			if (navigation && typeof navigation.then === 'function') {
				navigation.then(resolve, reject)
			}
		})
			.catch(() => {})
			.finally(() => router.go(0))
	},

	async login({ dispatch, commit }, credentials) {
		let access_token = null
		try {
			const data = await this.$axios.$post('login', credentials)
			access_token = data.access_token
		} catch (error) {
			return Promise.reject(error)
		}

		clearUserScopedState(commit)
		commit('setToken', access_token)

		return dispatch('getUser')
	},

	async register({ dispatch, commit }, data) {
		const { access_token } = await this.$axios.$post('register', data, {
			__skipGlobalErrorToast: true
		})
		clearUserScopedState(commit)
		commit('setToken', access_token)

		// Return user data for analytics tracking - APR-981
		return dispatch('getUser')
	},

	async logout({ commit }, options = {}) {
		try {
			// Try to logout on the server, but don't worry if it fails
			await this.$axios.$post('logout')
		} catch (error) {
			// Silently ignore any logout API errors
			// The user should still be logged out locally regardless
			console.warn(
				'Logout API call failed, continuing with local logout:',
				error.message
			)
		}

		// Clear cached data and invalidate pending work from the previous session.
		clearUserScopedState(commit)

		const preservedSecurityIncidentBannerDismissals = preserveSecurityIncidentBannerDismissals()

		// Clear localStorage completely for clean slate
		Storage.flush()

		restoreSecurityIncidentBannerDismissals(
			preservedSecurityIncidentBannerDismissals
		)

		this.$router.push(options.redirectTo || '/')
	},

	async leaveImpersonation({ commit, dispatch }) {
		let data
		try {
			data = await this.$axios.$post('impersonations/end')
		} catch (error) {
			if (error.response?.status === 400) {
				clearImpersonationMarker()

				error.impersonationEnded = true
			}

			throw error
		}

		commit('setToken', data.access_token)

		let user
		try {
			user = await dispatch('getUser')
		} catch (error) {
			clearImpersonationMarker()
			clearUserScopedState(commit)

			error.impersonationIdentityCleared = true
			this.$router.push('/auth/login')
			throw error
		}

		if (!user) {
			clearImpersonationMarker()
			clearUserScopedState(commit)

			const error = new Error(
				'Unable to restore user after impersonation'
			)
			error.impersonationIdentityCleared = true
			this.$router.push('/auth/login')
			throw error
		}

		clearImpersonationMarker()

		// Clear cached data from the impersonated user after the original user's
		// full resource payload is restored to avoid a transient broken render.
		commit('applicantProfile/setApplicantProfile', null, { root: true })
		commit('filterCriteria/setFilterCriteria', null, { root: true })
		if (!user.current_organization) {
			commit('organization/setOrganization', null, { root: true })
			commit('jobOffer/RESET_STATE', null, { root: true })
		} else {
			try {
				await dispatch('jobOffer/fetch', null, { root: true })
			} catch (error) {
				if (isExpectedAuthenticationFailure(error)) {
					error.impersonationIdentityCleared = true
					throw error
				}

				console.error(
					'Failed to preload job offers after leaving impersonation:',
					error
				)
			}
		}

		this.$router.replace('/dashboard')
	},

	async forgotPassword({ commit }, email) {
		return await this.$axios.$post('forgot-password', { email })
	},

	async resetPassword({ commit }, data) {
		return await this.$axios.$post('reset-password', data)
	},

	getUser({ commit, state, dispatch }, options = {}) {
		const {
			skipCredentials = false,
			skipOrganizationFetch = true,
			hydrateApplicantProfileFromUser = false,
			skipGlobalErrorToast = false
		} = options || {}
		const requestConfig = {
			__skipCredentials: skipCredentials
		}

		if (skipGlobalErrorToast) {
			requestConfig.__skipGlobalErrorToast = true
		}

		return this.$axios
			.$get('user', requestConfig)
			.then(data => {
				commit('setUser', data)

				if (hydrateApplicantProfileFromUser) {
					commit(
						'applicantProfile/setApplicantProfile',
						data.applicant_profile || null,
						{ root: true }
					)
				}

				// The user response already includes the active organization's access state.
				if (data.current_organization) {
					if (skipOrganizationFetch) {
						commit(
							'organization/setOrganization',
							data.current_organization,
							{ root: true }
						)

						return data
					}

					return dispatch(
						'organization/fetch',
						data.current_organization.id,
						{ root: true }
					)
						.catch(err => {
							if (err.response?.status === 401) {
								throw err
							}

							console.error('Failed to fetch organization:', err)
							// Non-auth organization refresh failures should not fail getUser.
						})
						.then(() => data)
				}

				// Return user data for analytics tracking - APR-981
				return data
			})
	},

	update({ commit }, data) {
		return this.$axios.$patch('user', data).then(data => {
			commit('setUser', data)
			commit('setLocale', data, { root: true })
		})
	},

	changePassword(
		_ctx,
		{ current_password, password, password_confirmation }
	) {
		return this.$axios.post(
			'user/change-password',
			{
				current_password,
				password,
				password_confirmation
			},
			{
				__skipGlobalErrorToast: true
			}
		)
	},

	async changeOrganization({ commit, dispatch }, organization) {
		if (!(await confirmTemplateNavigation())) return false
		return this.$axios
			.$get('user/change-organization/' + organization.id)
			.then(data => {
				commit('setUser', data)
				dispatch(
					'toast/push',
					{ title: `Zu ${organization.name} gewechselt` },
					{ root: true }
				)
				window.location.href = '/'
			})
			.catch(error => {
				resetTemplateNavigation()
				throw error
			})
	},

	leaveOrganization({ state, commit, dispatch }, organization) {
		return this.$axios
			.$get('user/leave-organization/' + organization.id)
			.then(async data => {
				let user = state.user
				user.organizations = user.organizations.filter(
					v => v.id !== organization.id
				)
				commit('setUser', user)

				window.location.href = '/'
			})
	},

	uploadAvatar({ commit, dispatch }, { file, onProgress = null }) {
		return dispatch(
			'media/upload',
			{
				file,
				field: 'avatar',
				endpoint: 'user/avatar',
				onProgress
			},
			{ root: true }
		).then(data => {
			commit('setUser', data)
		})
	},

	resendEmailVerification({ dispatch, state }) {
		return this.$axios
			.$post('user/resend-verification')
			.then(response => {
				if (response?.status === 'already_verified')
					throw new Error(
						this.app.i18n.t('user.email-already-verified')
					)
				dispatch(
					'toast/push',
					{
						title: this.app.i18n.t('user.verification-sent'),
						message: this.app.i18n.t(
							'user.verification-sent-text',
							{ email: state.user.email }
						),
						type: 'success'
					},
					{ root: true }
				)
			})
			.catch(() => {
				dispatch(
					'toast/push',
					{
						title: this.app.i18n.t('user.email-already-verified'),
						type: 'info'
					},
					{ root: true }
				)
			})
	}
}
